import { useEffect, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import GrayButton from "../GrayButton";
import Tooltip from "../Tooltip";
import {cancelReserved} from "../../api/message/volatility-message.api";
import {
  Button,
  CalendarBar,
  CalendarBlue,
  DocBlack,
  InfoOrange,
  Pagination,
  Tab,
  Table,
  Tag,
  WarningRed
} from "omoplay";
import {createAIMessage, listAIMessages} from "../../api/ai/ai.api";
import AIMessageBanner from "../../assets/images/ai_message_banner.svg"
import StarIcon from "../../assets/images/icon.star.svg"


const AIVolatilityMessageList = () => {
  // null = "전체"(기간 조건 없음). 초기 탭이 "전체" 이므로 null 로 시작한다.
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPages] = useState(0);
  const [tableData, setTableData] = useState([])

  const dateItems = [
    { value: "all", label: "전체", selected: true },
    { value: "month", label: "최근 한 달" },
    { value: "week", label: "최근 일주일" },
  ];

  const headers = [
    { name: "메시지 타입", sortable: false, width: "150px", minWidth: "150px" },
    {
      name: "발송 내역",
      sortable: false,
      minWidth: "286px"
    },
    {
      name: "성공 수 / 발송 수",
      sortable: false,
      align: "end",
      width: "190px",
      minWidth: "190px"
    },
    {
      name: "링크 클릭 수",
      sortable: false,
      align: "end",
      width: "110px",
      minWidth: "110px"
    },
    {
      name: "발송 일시",
      sortable: false,
      width: "200px",
      minWidth: "200px"
    },
    {
      name: "생성 / 수정 일시",
      sortable: false,
      width: "200px",
      minWidth: "200px"
    },
    { name: "상태", sortable: false, width: "150px", minWidth: "150px" },
  ];

  useEffect(() => {
    const renderName = (id, status, name, errText) => {
      const commonClass = "flex gap-2 items-center w-[245px] ";
      if (status === "RESERVED") {
        return (
            <div className={commonClass}>
              <div className={commonClass} style={{width: "unset"}}>
                <CalendarBlue />
                <div className="overflow-hidden whitespace-nowrap text-ellipsis w-[140px]">{name}</div>
              </div>
              <GrayButton
                  className="text-xs px-3"
                  onClick={() => { cancelReservedMessage(id)} }
              >
                예약 취소
              </GrayButton>
            </div>
        );
      } else if (status === "FAILED") {
        return (
            <Link to={"/ai/volatility-message/edit?id=" + id}>
              <div className={commonClass + "text-red-500"}>
                <Tooltip top={"top-4"} message={errText}>
                  <WarningRed />
                </Tooltip>
                <div className="overflow-hidden whitespace-nowrap text-ellipsis w-[140px]">{name}</div>
              </div>
            </Link>
        );
      } else if (status === "COUNTING") {
        return (
            <div className={commonClass + "text-indigo-600 cursor-pointer"}>
              <div className="overflow-hidden whitespace-nowrap text-ellipsis w-[140px]">{name}</div>
            </div>
        );
      } else if (status === "SUCCEED" || status === "SENDING") {
        return (
            <Link to={"/volatility-message/detail?id=" + id}>
              <div className={commonClass + "cursor-pointer"}>
                <DocBlack />
                <div className="overflow-hidden whitespace-nowrap text-ellipsis w-[140px]">{name}</div>
              </div>
            </Link>
        );
      } else if (status === "TEMPORARY") {
        return (
            <Link to={"/ai/volatility-message/edit?id=" + id}>
              <div className={commonClass + "text-gray-400"}>
                <InfoOrange />
                <div className="overflow-hidden whitespace-nowrap text-ellipsis w-[140px]">{name}</div>
              </div>
            </Link>
        );
      }
    };

    const renderType = (type) => {
      if (type === "TEXT" || type === "TEXT_IMAGE") {
        return "기본 텍스트형"
      } else if (type === "WIDE_IMAGE") {
        return "와이드 이미지형"
      } else if (type === "LIST_IMAGE") {
        return "와이드 리스트형"
      } else if (type === "CAROUSEL") {
        return "캐러셀 피드형"
      }
    }

    listAIMessages(dateRange?.startDate, dateRange?.endDate, page - 1, 10)
        .then(({data}) => {
          setTableData(data.messages.map(message => {
            // 친구톡 + 모먼트(비회원 broadcast) 합산. 모먼트는 moment_* 컬럼에 별도 저장됨.
            const totalSuccess = (message.successCount ?? 0) + (message.momentSuccessSendCount ?? 0)
            const totalSend = (message.sendCount ?? 0) + (message.momentSendCount ?? 0)
            const totalLink = (message.linkCount ?? 0) + (message.momentLinkCount ?? 0)
            return [
              <Tag>{renderType(message.messageType)}</Tag>,
              renderName(message.id, message.status, message.campaignName, message.errorText),
              <div className="text-right">
                {totalSuccess.toLocaleString()} / {totalSend.toLocaleString()} ({ totalSend === 0 ? "0" : Math.round(totalSuccess / totalSend * 100)}%)
              </div>,
              <div className="text-right">
                {totalLink.toLocaleString()} (
                {(totalLink && totalSuccess
                        ? Math.round((totalLink / totalSuccess) * 100)
                        : 0
                ).toLocaleString() + "%"}
                )
              </div>,
              moment(message.issuedAt).format("YYYY.MM.DD HH:mm"),
              moment(message.createdAt).format("YYYY.MM.DD HH:mm"),
              renderStatus(message.status),
            ]
          }))
          setTotalPages(data.count);
        })
  }, [dateRange, page]);

  const handleQuickChange = (type) => {
    const format = "YYYY-MM-DD"
    const today = moment().format(format)

    // "전체" = 기간 조건 없음. 종전에는 getAllDateRange()(= 최근 1년)를 보내 1년 전 메시지가 숨었다.

    if (type === "all") {
      setDateRange(null);
    } else if (type === "month") {
      setDateRange({
        startDate: moment().subtract(1, "months").format(format),
        endDate: today,
      });
    } else if (type === "week") {
      setDateRange({
        startDate: moment().subtract(6, "days").format(format),
        endDate: today,
      });
    }
  };

  const onDateInput = (dateRange) => {
    setDateRange({
      startDate: moment(dateRange.startDate).format("yyyy-MM-DD"),
      endDate: moment(dateRange.endDate).format("yyyy-MM-DD")
    });
  };

  const handleCreateClick = () => {
    createAIMessage(true).then(({data}) => {
      window.location.href = `/ai/volatility-message/edit?id=${data}`;
    })
  };

  const cancelReservedMessage = (id) => {
    if (window.confirm("예약을 취소하시겠습니까?\n임시 저장 상태로 변경됩니다.")) {
      cancelReserved(id).then(() => {
        window.location.reload()
      })
    }
  }

  const renderStatus = (status) => {
    if (status === "RESERVED") {
      return (<Tag color="info">발송 예약</Tag>)
    } else if (status === "FAILED") {
      return (<Tag color="error">발송 실패</Tag>)
    } else if (status === "SENDING") {
      return (<Tag color="success">발송 중</Tag>)
    } else if (status === "SUCCEED") {
      return (<Tag color="default">발송 완료</Tag>)
    } else if (status === "TEMPORARY") {
      return (<Tag color="warning">임시 저장</Tag>)
    }
  }

  return (
    <>
      <div>
        <img className="w-full" alt="ai-message-banner" src={AIMessageBanner} />
      </div>
      <div className="bg-white relative border border-gray-300 rounded-lg flex gap-4 mb-8 p-4 items-center" style={{marginTop: "35px"}}>
        <div className="flex gap-4">
          <div>
            <Tab
                variants="input"
                items={dateItems}
                onChanged={(data) => handleQuickChange(data.value)}
            />
          </div>
          {/* dateRange 가 null("전체") 이면 initDate 를 주지 않는다 → CalendarBar 가 '전체 ~ 전체' 로 표시한다.
              CalendarBar 는 initDate 가 truthy 일 때만 내부 상태를 갱신하므로, 날짜→전체 전환 때 이전 날짜가
              남지 않도록 key 로 다시 마운트한다. */}
          <CalendarBar key={dateRange ? "ranged" : "all"}
                       initDate={dateRange ? {startDate: moment(dateRange.startDate).toDate(), endDate: moment(dateRange.endDate).toDate()} : undefined}
                       onConfirm={(props) => onDateInput(props)} />
        </div>
      </div>
      <div className="flex-col bg-white rounded p-8 px-4 w-full">
        <div className="flex justify-end">
          <Button
              variants="ai"
              value={<div className="flex-align-center gap-1"><img src={StarIcon} alt="star" style={{width: "16px", height: "16px"}} />AI 자동 생성하기</div>}
              onClick={() => handleCreateClick(true)}
          />
        </div>
        <div className="flex-col py-4">
          <Table headers={headers} data={tableData} />
          <div className="flex justify-center" style={{marginTop: "24px"}}>
            <Pagination pageSize={10} totalCount={totalPage} currentPage={page} onPaging={(page) => setPage(page)} />
          </div>
        </div>
      </div>
    </>
  );
};
export default AIVolatilityMessageList;
